/**
 * Mobile menu keyboard accessibility fix.
 *
 * Keeps off-screen columns and collapsed accordion content out of the Tab order,
 * and lets Tab/Shift+Tab leave the menu cleanly at its boundaries.
 */
document.addEventListener('DOMContentLoaded', () => {
  const MOBILE_QUERY = '(max-width: 1023px)';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const isActuallyVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const getPageFocusable = () =>
    Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isActuallyVisible);

  document.querySelectorAll('.tooltip--mobile[data-tooltip]').forEach((dialog) => {
    const toggletip = dialog.closest('toggletip-engine');
    if (!toggletip) return;

    const trigger = toggletip.querySelector('[data-tooltip-trigger]');
    const innerContainer = dialog.querySelector('.nav--mobile__inner-container');
    const columns = innerContainer
      ? Array.from(innerContainer.querySelectorAll(':scope > .nav--mobile__column'))
      : [];

    if (!trigger) return;

    const setInteractive = (element, interactive) => {
      if (!(element instanceof HTMLElement)) return;
      element.inert = !interactive;
      element.setAttribute('aria-hidden', interactive ? 'false' : 'true');
    };

    const syncAccordionState = () => {
      dialog.querySelectorAll('.nav__mobile-dropdown-wrapper').forEach((wrapper) => {
        const button = wrapper.querySelector(':scope > .nav__mobile-accordion-opener');
        const content = wrapper.querySelector(':scope > .nav__mobile-dropdown__content-wrapper');
        const isOpen = wrapper.classList.contains('nav__mobile-dropdown-wrapper--open');

        if (button) button.setAttribute('aria-expanded', String(isOpen));
        if (content) setInteractive(content, isOpen);
      });
    };

    const syncActiveColumn = () => {
      if (columns.length < 2) return;

      const secondaryOpen = dialog.classList.contains('nav--secondary-menu-open');
      setInteractive(columns[0], !secondaryOpen);
      setInteractive(columns[1], secondaryOpen);
    };

    const syncMenuState = () => {
      syncActiveColumn();
      syncAccordionState();
    };

    const getMenuFocusable = () =>
      Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isActuallyVisible);

    const moveOutsideMenu = (backwards) => {
      const focusable = getPageFocusable().filter((element) => !dialog.contains(element));
      const triggerIndex = focusable.indexOf(trigger);
      const destination = backwards
        ? focusable[triggerIndex - 1]
        : focusable[triggerIndex + 1];

      if (typeof toggletip.close === 'function') {
        toggletip.close();
      } else {
        trigger.click();
      }

      window.requestAnimationFrame(() => {
        if (destination instanceof HTMLElement) destination.focus();
        else trigger.focus();
      });
    };

    const handleKeydown = (event) => {
      if (event.key !== 'Tab' || !window.matchMedia(MOBILE_QUERY).matches) return;

      syncMenuState();
      const focusable = getMenuFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        moveOutsideMenu(true);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        moveOutsideMenu(false);
      }
    };

    const openObserver = new MutationObserver(() => {
      const isOpen = trigger.getAttribute('open') === 'true';
      dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      if (isOpen) {
        syncMenuState();
        window.requestAnimationFrame(() => {
          const first = getMenuFocusable()[0];
          if (first) first.focus();
        });
      } else {
        columns.forEach((column) => {
          column.inert = false;
          column.removeAttribute('aria-hidden');
        });
      }
    });

    const menuObserver = new MutationObserver(syncMenuState);

    openObserver.observe(trigger, {
      attributes: true,
      attributeFilter: ['open']
    });

    menuObserver.observe(dialog, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    dialog.addEventListener('keydown', handleKeydown);
    syncMenuState();
  });
});
